import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import {
  Box,
  Chip,
  Dialog,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import type {
  IAvaliacaoPedido,
  IAvaliacaoProduto,
  StatusAvaliacao,
} from "interfaces/loja";
import {
  modalHeaderSx,
  modalPaperSx,
  saveButtonSx,
} from "../avaliacoes.theme";

function statusChipSx(status: StatusAvaliacao | undefined) {
  const s = status ?? "PENDENTE";
  if (s === "PUBLICADA") {
    return { bg: "rgba(16, 185, 129, 0.9)", fg: "#fff" as const };
  }
  if (s === "PENDENTE") {
    return { bg: "rgba(245, 158, 11, 0.9)", fg: "#fff" as const };
  }
  return { bg: "rgba(239, 68, 68, 0.9)", fg: "#fff" as const };
}

function formatDateTime(iso: string | undefined | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function StarsRow({ nota }: { nota: number | undefined | null }) {
  return (
    <MDBox display="flex" alignItems="center" gap={0.5}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = Number(nota) >= n;
        const StarCmp = filled ? StarIcon : StarBorderIcon;
        return (
          <StarCmp
            key={n}
            sx={{
              fontSize: 32,
              color: filled ? "warning.main" : "text.secondary",
            }}
          />
        );
      })}
      <MDTypography variant="body1" fontWeight="bold" ml={1}>
        {nota ?? 0} / 5
      </MDTypography>
    </MDBox>
  );
}

type ProdutoProps = {
  tipo: "produto";
  data: IAvaliacaoProduto;
  onStatusChange: (id: string, status: StatusAvaliacao, label: string) => void;
};

type PedidoProps = {
  tipo: "pedido";
  data: IAvaliacaoPedido;
  pedidoLabel: string;
  onStatusChange: (id: string, status: StatusAvaliacao, label: string) => void;
};

interface BaseProps {
  open: boolean;
  savingId: string | null;
  onClose: () => void;
}

type Props = BaseProps & (ProdutoProps | PedidoProps);

export default function AvaliacaoDetailDialog(props: Props) {
  const { open, savingId, onClose } = props;
  if (!open) return null;

  const data = props.data;
  const st = data.status;
  const chip = statusChipSx(st);
  const isProd = props.tipo === "produto";
  const title = isProd ? "Avaliação de produto" : "Avaliação de pedido";
  const subtitle = isProd
    ? (data as IAvaliacaoProduto).produto?.nome ?? (data as IAvaliacaoProduto).produtoId
    : props.tipo === "pedido"
      ? props.pedidoLabel
      : "";

  const handleStatus = (status: StatusAvaliacao, label: string) => {
    props.onStatusChange(data.id, status, label);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      PaperProps={{ sx: modalPaperSx }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" minWidth={0}>
            <RateReviewOutlinedIcon sx={{ fontSize: 36, opacity: 0.9 }} />
            <Box minWidth={0}>
              <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
                {title}
              </Typography>
              <Typography variant="h5" fontWeight="bold" noWrap>
                {subtitle}
              </Typography>
              <Box mt={0.75}>
                <Chip
                  size="small"
                  label={st}
                  sx={{ bgcolor: chip.bg, color: chip.fg, fontWeight: 600 }}
                />
              </Box>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <MDBox sx={{ p: 3, overflowY: "auto" }}>
        <Stack spacing={2}>
          {isProd ? (
            <>
              <InfoBlock
                label="Produto"
                value={(data as IAvaliacaoProduto).produto?.nome ?? (data as IAvaliacaoProduto).produtoId}
              />
              <InfoBlock
                label="Cliente"
                value={(data as IAvaliacaoProduto).usuario?.nome ?? data.usuarioId}
              />
            </>
          ) : (
            <>
              <InfoBlock
                label="Pedido"
                value={`${props.pedidoLabel} · ID: ${(data as IAvaliacaoPedido).pedidoId}`}
              />
              <InfoBlock
                label="Cliente"
                value={(data as IAvaliacaoPedido).usuario?.nome ?? data.usuarioId}
              />
            </>
          )}

          <Box>
            <MDTypography variant="caption" color="text.secondary" fontWeight="bold">
              Nota
            </MDTypography>
            <StarsRow nota={data.nota} />
          </Box>

          <Divider />

          {isProd && (data as IAvaliacaoProduto).titulo ? (
            <InfoBlock label="Título" value={(data as IAvaliacaoProduto).titulo ?? "—"} large />
          ) : null}

          <Box>
            <MDTypography variant="caption" color="text.secondary" fontWeight="bold">
              Comentário
            </MDTypography>
            <MDBox
              sx={{
                mt: 1,
                p: 2,
                borderRadius: 1.5,
                bgcolor: "grey.50",
                border: "1px solid",
                borderColor: "grey.200",
                maxHeight: "min(360px, 50vh)",
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              <MDTypography variant="body2" component="div">
                {data.comentario?.trim() || "—"}
              </MDTypography>
            </MDBox>
          </Box>

          <Stack direction="row" flexWrap="wrap" gap={2}>
            <MDTypography variant="caption" color="text.secondary">
              Criada em: {formatDateTime(data.createAt)}
            </MDTypography>
            {data.updatedAt ? (
              <MDTypography variant="caption" color="text.secondary">
                Atualizada em: {formatDateTime(data.updatedAt)}
              </MDTypography>
            ) : null}
          </Stack>
        </Stack>
      </MDBox>

      <MDBox
        px={3}
        py={2}
        display="flex"
        flexWrap="wrap"
        gap={1}
        justifyContent="flex-end"
        borderTop="1px solid"
        borderColor="grey.200"
      >
        <MDButton type="button" variant="outlined" color="dark" onClick={onClose}>
          Fechar
        </MDButton>
        <MDButton
          type="button"
          variant="outlined"
          color="warning"
          disabled={st === "PENDENTE" || savingId === data.id}
          onClick={() => handleStatus("PENDENTE", "marcada como pendente")}
        >
          Pendente
        </MDButton>
        <MDButton
          type="button"
          variant="outlined"
          color="error"
          disabled={st === "REJEITADA" || savingId === data.id}
          onClick={() => handleStatus("REJEITADA", "rejeitada")}
        >
          Rejeitar
        </MDButton>
        <MDButton
          type="button"
          variant="gradient"
          color="dark"
          disabled={st === "PUBLICADA" || savingId === data.id}
          onClick={() =>
            handleStatus(
              "PUBLICADA",
              isProd ? "publicada na loja" : "publicada",
            )
          }
          sx={saveButtonSx}
        >
          {isProd ? "Publicar na loja" : "Publicar"}
        </MDButton>
      </MDBox>
    </Dialog>
  );
}

function InfoBlock({
  label,
  value,
  large,
}: {
  label: string;
  value: string;
  large?: boolean;
}) {
  return (
    <Box>
      <MDTypography variant="caption" color="text.secondary" fontWeight="bold">
        {label}
      </MDTypography>
      <MDTypography variant={large ? "h6" : "body2"} sx={{ mt: 0.25 }}>
        {value}
      </MDTypography>
    </Box>
  );
}
